/**
 * Lead qualification for Custom / Enterprise inquiries (meeting 2026-09-28).
 *
 * Every inquiry is qualified. Two inputs:
 *  - the fit tier the intake form computed (enterprise-intake/fit-score.js). It
 *    has no column; the form writes it as line 1 of `details`
 *    ("Fit: qualified, 9/10 (...)").
 *  - the budget, which the form deliberately does not ask (Eli: budget is a
 *    call topic). Staff record it after the call; the form's "Average deal
 *    value" answer is only a hint until then.
 *
 * Rule from the meeting: under a $1,000 budget we don't quote a custom build;
 * we point them to Basic / Pro or to prebuilt n8n workflows / Claude plugins.
 */

export type FitTier = 'qualified' | 'maybe' | 'not_a_fit'

export const PREBUILT_BUDGET_LIMIT_USD = 1000

export type Qualification = {
  tier: FitTier | null
  score: number | null
  budgetUsd: number | null
  dealValue: string | null
  route: 'custom_build' | 'prebuilt' | 'unknown'
  reason: string
}

const FIT_RE = /^Fit:\s*(qualified|maybe|not_a_fit),\s*(\d+)\/10/m
const DEAL_RE = /^Average deal value:\s*(.+)$/m

/** First dollar figure in a free-text budget ("$800/mo", "1.5k", "2,000"). */
export function parseBudgetUsd(raw: string | null | undefined): number | null {
  if (!raw) return null
  const m = raw.replace(/,/g, '').match(/\$?\s*(\d+(?:\.\d+)?)\s*(k)?/i)
  if (!m) return null
  const n = Number(m[1]) * (m[2] ? 1000 : 1)
  return Number.isFinite(n) ? n : null
}

export function qualifyInquiry(q: { details: string | null; budget: string | null }): Qualification {
  const fit = q.details?.match(FIT_RE)
  const tier = (fit?.[1] as FitTier | undefined) ?? null
  const score = fit ? Number(fit[2]) : null
  const dealValue = q.details?.match(DEAL_RE)?.[1]?.trim() ?? null
  const budgetUsd = parseBudgetUsd(q.budget)

  if (budgetUsd !== null) {
    return budgetUsd < PREBUILT_BUDGET_LIMIT_USD
      ? { tier, score, budgetUsd, dealValue, route: 'prebuilt', reason: `Budget under $${PREBUILT_BUDGET_LIMIT_USD.toLocaleString()}: don't quote a custom build. Point them to Basic / Pro or a prebuilt n8n workflow or Claude plugin.` }
      : { tier, score, budgetUsd, dealValue, route: 'custom_build', reason: 'Budget supports a custom build. Quote after the call.' }
  }
  if (tier === 'not_a_fit') {
    return { tier, score, budgetUsd, dealValue, route: 'prebuilt', reason: 'Form scored not a fit. Point them to Basic / Pro or a prebuilt workflow unless the call shows otherwise.' }
  }
  const hint = dealValue === 'Under $1,000' ? ' Deal value is under $1,000, so check budget early.' : ''
  return { tier, score, budgetUsd, dealValue, route: 'unknown', reason: `Ask the budget on the call and record it here.${hint}` }
}
